import { NopPositionRow, NopPositionTableProps } from '@/components/trader-modules/Blotters/NopPositionTable';
import { GroupBy } from '@/components/trader-modules/Blotters/PositionGroupSwitch';
import { convertedQtyFormatterConfig, defaultColumn } from '@/config/config';
import { nopPositionColumns } from '@/helpers/nopPositionHelper';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDebouncedValue } from '@/utils/hooks/useDebouncedValue';
import {
    ColumnDef,
    ExpandedState,
    filterFns,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { useDidUpdate } from './useDidUpdate';
import useLanguage from './useLanguage';
import useNopPositions from './useNopPositions';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

const basicGrouping = (item: NopPositionRow, groupedMap) => {
    const {
        instrument,
        tableInstrument,
        type,
        market,
        amount,
        openPrice,
        currentPrice,
        unrealisedPnl,
        unrealisedPnlInBaseCurrency,
        settlementDate,
        accountCode
    } = item;
    if (groupedMap.has(tableInstrument)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const group = groupedMap.get(tableInstrument)!;

        const convertedPosition = BigNumber(amount).multipliedBy(openPrice).toNumber();
        const sumOfNetPosition = BigNumber(group.amount).plus(amount).toNumber();

        group.openPrice =
            sumOfNetPosition !== 0
                ? BigNumber(convertedPosition)
                      .plus(BigNumber(group.openPrice).multipliedBy(group.amount))
                      .dividedBy(sumOfNetPosition)
                      .toNumber()
                : '-';
        group.currentPrice = currentPrice;
        group.amount = BigNumber(group.amount).plus(amount).toNumber();

        // group.unrealisedPnl = {
        //     value: BigNumber(group.unrealisedPnl?.value || 0)
        //         .plus(unrealisedPnl.value || 0)
        //         .toNumber(),
        //     currency: unrealisedPnl.currency
        // };
        if (group.unrealisedPnl?.value === null) {
            group.unrealisedPnl = {
                value: unrealisedPnl.value,
                currency: unrealisedPnl.currency
            };
        } else {
            group.unrealisedPnl = {
                value: BigNumber(group.unrealisedPnl?.value || 0)
                    .plus(unrealisedPnl.value || 0)
                    .toNumber(),
                currency: unrealisedPnl.currency
            };
        }
        if (group.unrealisedPnlInBaseCurrency?.value === null) {
            group.unrealisedPnlInBaseCurrency = {
                value: unrealisedPnlInBaseCurrency.value,
                currency: unrealisedPnlInBaseCurrency.currency
            };
        } else {
            group.unrealisedPnlInBaseCurrency = {
                value: BigNumber(group.unrealisedPnlInBaseCurrency?.value || 0)
                    .plus(unrealisedPnlInBaseCurrency.value || 0)
                    .toNumber(),
                currency: unrealisedPnlInBaseCurrency.currency
            };
        }

        group.subRows.push({
            instrument,
            tableInstrument: `VD: ${settlementDate}`,
            type,
            amount,
            openPrice,
            currentPrice,
            unrealisedPnl,
            unrealisedPnlInBaseCurrency,
            settlementDate,
            accountCode
        });
    } else {
        groupedMap.set(tableInstrument, {
            instrument,
            tableInstrument,
            market,
            type,
            amount,
            openPrice,
            currentPrice,
            unrealisedPnl,
            unrealisedPnlInBaseCurrency,
            settlementDate: '',
            accountCode,
            subRows: [
                {
                    instrument,
                    tableInstrument: `VD: ${settlementDate}`,
                    type,
                    amount,
                    openPrice,
                    currentPrice,
                    unrealisedPnl,
                    unrealisedPnlInBaseCurrency,
                    settlementDate,
                    accountCode
                }
            ]
        });
    }
};

const groupRowsByInstrument = (rows: NopPositionRow[], groupBy) => {
    const groupedMap = new Map<string, any>();
    const productGroupedMap = new Map<string, any>();

    if (groupBy === 'None') {
        rows.forEach((item) => basicGrouping(item, groupedMap));
        const returnValues = Array.from(groupedMap.values());

        return returnValues;
    } else if (groupBy === 'Products') {
        rows.forEach((item) => {
            const {
                instrument,
                tableInstrument,
                type,
                market,
                amount,
                openPrice,
                currentPrice,
                unrealisedPnl,
                unrealisedPnlInBaseCurrency,
                settlementDate,
                accountCode
            } = item;

            if (productGroupedMap.has(type)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const productGroup = productGroupedMap.get(type)!;
                // productGroup.unrealisedPnl = {
                //     value: BigNumber(productGroup.unrealisedPnl?.value || 0)
                //         .plus(unrealisedPnl.value || 0)
                //         .toNumber(),
                //     currency: unrealisedPnl.currency
                // };
                if (productGroup.unrealisedPnl?.value === null) {
                    productGroup.unrealisedPnl = {
                        value: unrealisedPnl.value,
                        currency: unrealisedPnl.currency
                    };
                } else {
                    productGroup.unrealisedPnl = {
                        value: BigNumber(productGroup.unrealisedPnl?.value || 0)
                            .plus(unrealisedPnl.value || 0)
                            .toNumber(),
                        currency: unrealisedPnl.currency
                    };
                }
                if (productGroup.unrealisedPnlInBaseCurrency?.value === null) {
                    productGroup.unrealisedPnlInBaseCurrency = {
                        value: unrealisedPnlInBaseCurrency.value,
                        currency: unrealisedPnlInBaseCurrency.currency
                    };
                } else {
                    productGroup.unrealisedPnlInBaseCurrency = {
                        value: BigNumber(productGroup.unrealisedPnlInBaseCurrency?.value || 0)
                            .plus(unrealisedPnlInBaseCurrency.value || 0)
                            .toNumber(),
                        currency: unrealisedPnlInBaseCurrency.currency
                    };
                }
                basicGrouping(item, productGroup.subRows);
            } else {
                productGroupedMap.set(type, {
                    instrument,
                    tableInstrument,
                    market,
                    type,
                    amount,
                    openPrice,
                    currentPrice,
                    unrealisedPnl,
                    unrealisedPnlInBaseCurrency,
                    settlementDate,
                    accountCode,
                    subRows: new Map<string, any>().set(tableInstrument, {
                        instrument,
                        tableInstrument,
                        market,
                        type,
                        amount,
                        openPrice,
                        currentPrice,
                        unrealisedPnl,
                        unrealisedPnlInBaseCurrency,
                        settlementDate: '',
                        accountCode,
                        subRows: [
                            {
                                instrument,
                                tableInstrument: `VD: ${settlementDate}`,
                                type,
                                amount,
                                openPrice,
                                currentPrice,
                                unrealisedPnl,
                                unrealisedPnlInBaseCurrency,
                                settlementDate,
                                accountCode
                            }
                        ]
                    })
                });
            }
        });
        const returnValues = Array.from(productGroupedMap.keys()).map((key) => {
            const productGroup = productGroupedMap.get(key);
            return {
                instrument: key,
                tableInstrument: key,
                market: key,
                type: key,
                amount: key,
                openPrice: key,
                currentPrice: key,
                unrealisedPnl: productGroup.unrealisedPnl,
                unrealisedPnlInBaseCurrency: productGroup.unrealisedPnlInBaseCurrency,
                settlementDate: key,
                accountCode: key,
                subRows: Array.from(productGroup.subRows.values())
            };
        });

        // Remove instrument subRows if length is 1
        // returnValues.forEach((group) => {
        //     group.subRows.forEach((item: any) => {
        //         if (item.subRows.length === 1) {
        //             item.subRows = [];
        //         }
        //     });
        // });

        return returnValues;
    }
    return [];
};

interface UseNopPositionTableProps extends NopPositionTableProps {
    groupBy: GroupBy;
    setInstrumentInfoContext: React.Dispatch<React.SetStateAction<any>>;
    handleOpenContextMenu: (row: any, x: number, y: number) => void;
}

const useNopPositionTable = (props: UseNopPositionTableProps) => {
    const { groupBy, setCSVData, onOrderModal, setInstrumentInfoContext, handleOpenContextMenu } = props;

    const currentAccount = useAppSelector(selectCurrentAccount);
    const formatQty = useNumberFormatter(convertedQtyFormatterConfig);
    const formatConvertedQty = useNumberFormatter(convertedQtyFormatterConfig);

    const lang = useLanguage();

    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });

    const nopPositions = useNopPositions({});
    const { nopPositionsHydated, debouncedNopPositions } = nopPositions;

    const baseCurrency = useMemo(() => debouncedNopPositions?.baseCurrency || 'USD', [debouncedNopPositions]);

    const columns: ColumnDef<NopPositionRow>[] = useMemo(() => {
        return nopPositionColumns({
            lang,
            baseCurrency,
            onOrderModal,
            groupBy,
            formatQty,
            formatConvertedQty,
            setInstrumentInfoContext,
            handleOpenContextMenu
        });
    }, [
        lang,
        baseCurrency,
        onOrderModal,
        groupBy,
        formatQty,
        formatConvertedQty,
        setInstrumentInfoContext,
        handleOpenContextMenu
    ]);

    const formattedNopPositions: any[] = useMemo(() => {
        const returnValues = nopPositions.formattedNopPositions;

        returnValues.sort((a, b) => {
            // Primary: instrument name ascending
            if (a.tableInstrument < b.tableInstrument) return -1;
            if (a.tableInstrument > b.tableInstrument) return 1;

            // Secondary: settlementDate descending (latest first)
            const dateA = new Date(a.settlementDate);
            const dateB = new Date(b.settlementDate);

            if (dateA > dateB) return -1; // latest first
            if (dateA < dateB) return 1;
            return 0;
        });

        return groupRowsByInstrument(returnValues, groupBy);
    }, [nopPositions.formattedNopPositions]);

    const tableInstance = useReactTable({
        data: formattedNopPositions,
        columns,
        state: { expanded: expandedRows, globalFilter },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        onExpandedChange: setExpandedRows,
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    useEffect(() => {
        setCSVData(formattedNopPositions);
    }, [formattedNopPositions]);

    useDidUpdate(() => {
        if (groupBy === 'None') {
            setExpandedRows({});
        } else if (groupBy === 'Products') {
            const newExpandedRows = {};
            formattedNopPositions.forEach((row, i) => {
                newExpandedRows[i] = true;
            });
            setExpandedRows(newExpandedRows);
        }
    }, [groupBy, nopPositionsHydated, currentAccount]);

    return { debouncedTableInstance, debouncedNopPositions, expandedRows, globalFilter };
};

export default useNopPositionTable;
